import React from 'react'
import { BrandLogo } from '@marketing-milk/interfaces'
import { radius, SurfacePalette, type } from '../../tokens'
import { brandFontStack, useBrandFonts } from '../../useBrandFonts'

interface Props {
  palette: SurfacePalette
  brandLogos: BrandLogo[]
  fonts: string[]
  colors: string[]
  editable?: boolean
  onEdit?: () => void
}

/**
 * Shared Brand Standards display, mirroring the legacy deck slide: logos on the
 * left (the anchor of the slide — each on a white card so dark logos read on the
 * dark Present deck), fonts and color swatches stacked on the right. Lists are
 * flexible; layout is weighted for the typical 1 logo / 2–3 fonts / 3–5 colors
 * and wraps gracefully beyond that. Font names render in their own typeface via
 * Google Fonts when available, falling back to the packet sans stack.
 */
export const BrandStandardsContent = ({
  palette,
  brandLogos,
  fonts,
  colors,
  editable,
  onEdit,
}: Props) => {
  useBrandFonts(fonts)

  const isEmpty = brandLogos.length === 0 && fonts.length === 0 && colors.length === 0

  if (isEmpty) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 12,
          color: palette.textMuted,
          fontSize: type.size.body,
        }}
      >
        <p style={{ margin: 0 }}>
          No brand standards yet
          {editable ? ' — add the client’s logos, fonts, and colors.' : '.'}
        </p>
        {editable && onEdit && (
          <button type="button" onClick={onEdit} style={editBtn(palette)}>
            Add brand standards
          </button>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {brandLogos.length > 0 && (
          <div style={{ flex: '1 1 380px', minWidth: 280 }}>
            <GroupLabel palette={palette}>Logos</GroupLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {brandLogos.map(logo => (
                <div
                  key={logo.key}
                  style={{
                    background: '#ffffff',
                    border: `1px solid ${palette.hairline}`,
                    borderRadius: radius.md,
                    padding: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={logo.url}
                    alt="Brand logo"
                    style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            flex: '1 1 320px',
            minWidth: 260,
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          {fonts.length > 0 && (
            <div>
              <GroupLabel palette={palette}>Fonts</GroupLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {fonts.map((font, i) => (
                  <div
                    key={font}
                    style={{
                      fontFamily: brandFontStack(font, type.family.sans),
                      // First font is implicitly primary — render it larger.
                      fontSize: i === 0 ? type.size.h1 : type.size.h2,
                      lineHeight: 1.2,
                      color: palette.text,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {font}
                  </div>
                ))}
              </div>
            </div>
          )}

          {colors.length > 0 && (
            <div>
              <GroupLabel palette={palette}>Colors</GroupLabel>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {colors.map(color => (
                  <div
                    key={color}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: radius.sm,
                        background: color,
                        border: `1px solid ${palette.hairline}`,
                      }}
                    />
                    <span
                      style={{
                        fontFamily: type.family.mono,
                        fontSize: type.size.eyebrow,
                        color: palette.textMuted,
                      }}
                    >
                      {color}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {editable && onEdit && (
        <div>
          <button type="button" onClick={onEdit} style={editBtn(palette)}>
            Edit brand standards
          </button>
        </div>
      )}
    </div>
  )
}

const GroupLabel = ({
  palette,
  children,
}: {
  palette: SurfacePalette
  children: React.ReactNode
}) => (
  <div
    style={{
      fontFamily: type.family.mono,
      fontSize: type.size.eyebrow,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: palette.accent,
      borderBottom: `1px solid ${palette.hairline}`,
      paddingBottom: 6,
      marginBottom: 14,
    }}
  >
    {children}
  </div>
)

const editBtn = (palette: SurfacePalette): React.CSSProperties => ({
  border: `1px solid ${palette.hairline}`,
  background: 'transparent',
  color: palette.textMuted,
  borderRadius: radius.sm,
  padding: '8px 16px',
  fontFamily: type.family.sans,
  fontSize: type.size.body,
  cursor: 'pointer',
})
